/**
 * Parses the "Format dataset" import workbook (a two-row header) into a structured
 * shape the matcher and plan-builder can consume.
 *
 * Input is the raw grid as produced by `xlsx`'s `sheet_to_json(ws, { header: 1 })`:
 * an array of rows, each row an array of cell values. Row 0 is the effect band row
 * (an effect name sits above the FIRST question column of its group, blanks across
 * the rest), row 1 is the column-header row (the five meta headers, then per effect
 * a band of question labels). Rows 2+ are respondent data rows.
 *
 * @typedef {object} ParsedQuestion
 * @property {number} colIndex - Zero-based column index of this question in every data row.
 * @property {string} questionName - Raw row-2 label (NOT normalized; matching normalizes later).
 *
 * @typedef {object} ParsedEffectBand
 * @property {string} effectName - Trimmed row-1 effect name that opens this band.
 * @property {ParsedQuestion[]} questions - Question columns belonging to this effect.
 *
 * @typedef {object} MetaCols
 * @property {number} personaId
 * @property {number} [organisatieId]
 * @property {number} [timestamp]
 * @property {number} [meetmoment]
 * @property {number} [version]
 *
 * @typedef {object} ParsedWorkbook
 * @property {MetaCols} metaCols - Column indices of the meta fields, keyed by role.
 * @property {ParsedEffectBand[]} effectBands - Effect groups with their question columns.
 * @property {Array<Array<*>>} dataRows - The respondent rows (everything after the header).
 */

/** @type {(v: *) => string} */
const normalize = (v) =>
    String(v ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

/** Maps a normalized header to its meta role, or null when it isn't a known meta column. */
const META_HEADER_ROLE = {
    personaid: 'personaId',
    organisatieid: 'organisatieId',
    timestamp: 'timestamp',
    meetmoment: 'meetmoment',
    'meetstandaard versie': 'version',
};

/**
 * Resolves a header cell to its meta role. Exports add clarifying suffixes to meta headers
 * (e.g. "Timestamp (submit time)"); a trailing parenthetical is stripped so the core label
 * still matches. Without this, an unmatched Timestamp column drops every response's date and
 * collapses all respondents into a single form.
 *
 * @param {*} headerCell - Raw row-1 header cell value.
 * @returns {string|null} The meta role, or null when the header isn't a known meta column.
 */
const resolveMetaRole = (headerCell) => {
    const normalized = normalize(headerCell);
    if (META_HEADER_ROLE[normalized]) return META_HEADER_ROLE[normalized];
    const withoutSuffix = normalized.replace(/\s*\([^)]*\)\s*$/, '').trim();
    return META_HEADER_ROLE[withoutSuffix] || null;
};

/** @type {(v: *) => boolean} */
const isBlank = (v) => v === undefined || v === null || String(v).trim() === '';

/**
 * @param {Array<Array<*>>} rows - The full sheet grid (header rows + data rows).
 * @returns {ParsedWorkbook}
 * @throws {Error} When the sheet is too short, has no PersonaID column, or has no effect bands.
 */
const parseDatasetWorkbook = (rows) => {
    if (!Array.isArray(rows) || rows.length < 2) {
        throw new Error('Importbestand is leeg of mist de twee koprijen (effect- en vraagrij).');
    }

    const bandRow = rows[0] || [];
    const headerRow = rows[1] || [];
    const width = Math.max(bandRow.length, headerRow.length);

    // The question region starts at the first column that carries an effect name in row 1.
    let questionStart = -1;
    for (let c = 0; c < width; c += 1) {
        if (!isBlank(bandRow[c])) {
            questionStart = c;
            break;
        }
    }
    if (questionStart === -1) {
        throw new Error('Geen effecten gevonden: de bovenste rij bevat geen effectnamen.');
    }

    // Meta columns are everything left of the question region, mapped by header text.
    /** @type {MetaCols} */
    const metaCols = {};
    for (let c = 0; c < questionStart; c += 1) {
        const role = resolveMetaRole(headerRow[c]);
        if (role) metaCols[role] = c;
    }
    if (metaCols.personaId === undefined) {
        throw new Error('Geen "PersonaID"-kolom gevonden in het importbestand.');
    }

    // Effect bands: each non-empty row-1 cell opens a band; question columns flow into it.
    /** @type {ParsedEffectBand[]} */
    const effectBands = [];
    let current = null;
    for (let c = questionStart; c < width; c += 1) {
        if (!isBlank(bandRow[c])) {
            current = { effectName: String(bandRow[c]).trim(), questions: [] };
            effectBands.push(current);
        }
        if (current && !isBlank(headerRow[c])) {
            current.questions.push({ colIndex: c, questionName: String(headerRow[c]) });
        }
    }

    return {
        metaCols,
        effectBands,
        dataRows: rows.slice(2),
    };
};

export default parseDatasetWorkbook;
